import * as React from "react";

export type DeviceCategory = "phone" | "tablet" | "laptop" | "desktop" | "ultrawide";
export type Orientation = "portrait" | "landscape";

export interface DeviceInfo {
  // Screen dimensions
  width: number;
  height: number;
  
  // Aspect ratio
  aspectRatio: number;
  aspectRatioLabel: string; // e.g., "16:9", "4:3", "21:9"
  
  // Orientation
  orientation: Orientation;
  
  // Device category
  deviceCategory: DeviceCategory;
  
  // Convenience booleans
  isPhone: boolean;
  isTablet: boolean;
  isLaptop: boolean;
  isDesktop: boolean;
  isUltrawide: boolean;
  isPortrait: boolean;
  isLandscape: boolean;
  
  // Pixel density
  pixelRatio: number;
  isRetina: boolean;
  
  // Touch capability
  isTouchDevice: boolean;
  
  // Accessibility
  prefersReducedMotion: boolean;
}

const BREAKPOINTS = {
  phone: 768,
  tablet: 1024,
  laptop: 1440,
  desktop: 1920,
};

function getDeviceCategory(width: number): DeviceCategory {
  if (width < BREAKPOINTS.phone) return "phone";
  if (width < BREAKPOINTS.tablet) return "tablet";
  if (width < BREAKPOINTS.laptop) return "laptop";
  if (width < BREAKPOINTS.desktop) return "desktop";
  return "ultrawide";
}

function getAspectRatioLabel(ratio: number): string {
  // Common aspect ratios
  if (Math.abs(ratio - 16/9) < 0.1) return "16:9";
  if (Math.abs(ratio - 4/3) < 0.1) return "4:3";
  if (Math.abs(ratio - 21/9) < 0.1) return "21:9";
  if (Math.abs(ratio - 3/2) < 0.1) return "3:2";
  if (Math.abs(ratio - 16/10) < 0.1) return "16:10";
  if (Math.abs(ratio - 9/16) < 0.1) return "9:16";
  if (Math.abs(ratio - 3/4) < 0.1) return "3:4";
  if (Math.abs(ratio - 1) < 0.1) return "1:1";
  return `${ratio.toFixed(2)}:1`;
}

function getOrientation(width: number, height: number): Orientation {
  return width >= height ? "landscape" : "portrait";
}

function calculateDeviceInfo(): DeviceInfo {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const aspectRatio = width / height;
  const deviceCategory = getDeviceCategory(width);
  const orientation = getOrientation(width, height);
  const pixelRatio = window.devicePixelRatio || 1;
  const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  
  return {
    width,
    height,
    aspectRatio,
    aspectRatioLabel: getAspectRatioLabel(aspectRatio),
    orientation,
    deviceCategory,
    isPhone: deviceCategory === "phone",
    isTablet: deviceCategory === "tablet",
    isLaptop: deviceCategory === "laptop",
    isDesktop: deviceCategory === "desktop",
    isUltrawide: deviceCategory === "ultrawide",
    isPortrait: orientation === "portrait",
    isLandscape: orientation === "landscape",
    pixelRatio,
    isRetina: pixelRatio >= 2,
    isTouchDevice: "ontouchstart" in window || navigator.maxTouchPoints > 0,
    prefersReducedMotion,
  };
}

export function useDeviceInfo(): DeviceInfo {
  const [deviceInfo, setDeviceInfo] = React.useState<DeviceInfo>({
    width: 0,
    height: 0,
    aspectRatio: 1,
    aspectRatioLabel: "1:1",
    orientation: "landscape",
    deviceCategory: "desktop",
    isPhone: false,
    isTablet: false,
    isLaptop: false,
    isDesktop: true,
    isUltrawide: false,
    isPortrait: false,
    isLandscape: true,
    pixelRatio: 1,
    isRetina: false,
    isTouchDevice: false,
    prefersReducedMotion: false,
  });

  React.useEffect(() => {
    // Use requestAnimationFrame to batch layout reads and avoid forced reflows
    let rafId: number;
    
    const updateDeviceInfo = () => {
      rafId = requestAnimationFrame(() => {
        setDeviceInfo(calculateDeviceInfo());
      });
    };

    // Initial calculation - deferred to avoid blocking render
    updateDeviceInfo();

    // Debounced resize handler to reduce reflow frequency
    let resizeTimeout: number;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(updateDeviceInfo, 100);
    };

    // Listen to resize events with passive flag
    window.addEventListener("resize", handleResize, { passive: true });
    
    // Listen to orientation change events (mobile/tablet)
    window.addEventListener("orientationchange", updateDeviceInfo, { passive: true });
    
    // Also use matchMedia for more reliable orientation detection
    const portraitQuery = window.matchMedia("(orientation: portrait)");
    portraitQuery.addEventListener("change", updateDeviceInfo);

    // Listen to reduced motion preference changes
    const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedMotionQuery.addEventListener("change", updateDeviceInfo);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", updateDeviceInfo);
      portraitQuery.removeEventListener("change", updateDeviceInfo);
      reducedMotionQuery.removeEventListener("change", updateDeviceInfo);
    };
  }, []);

  return deviceInfo;
}

// Convenience hooks for specific use cases
export function useOrientation(): Orientation {
  const { orientation } = useDeviceInfo();
  return orientation;
}

export function useDeviceCategory(): DeviceCategory {
  const { deviceCategory } = useDeviceInfo();
  return deviceCategory;
}

export function useScreenSize(): { width: number; height: number } {
  const { width, height } = useDeviceInfo();
  return { width, height };
}

export function usePrefersReducedMotion(): boolean {
  const { prefersReducedMotion } = useDeviceInfo();
  return prefersReducedMotion;
}
